import { useMemo } from 'react';
import { Autocomplete, Box, Button, Divider, IconButton, MenuItem, Stack, SvgIcon, TextField, Tooltip, Typography } from '@mui/material';
import { AudioFileConditionSet, ConditionType } from '@aviation/cabinapp-cabincast-sdk';
import { ulid } from 'ulidx';
import { useFetch } from '../../../hooks/useFetch';
import { ReactComponent as PlusIcon } from '../../../icons/plus.svg';
import { ReactComponent as TrashIcon } from '../../../icons/trash.svg';

// condition as edited in the UI. Multiple selected values are stored comma separated in `Value`.
export interface IConditionDraft {
    key: string;
    Type: ConditionType;
    Value: string;
}

export interface IConditionSetDraft {
    key: string;
    Conditions: IConditionDraft[];
}

export interface IConditionSetsEditorProps {
    value: IConditionSetDraft[];
    onChange: (value: IConditionSetDraft[]) => void;
    // Highlights conditions without a value. 
    showErrors?: boolean;
}

const VALUE_SEPARATOR = ',';

const CONDITION_TYPES: { type: ConditionType, label: string }[] = [
    { type: 'airline', label: 'Airline' },
    { type: 'dep', label: 'Departure' },
    { type: 'dest', label: 'Destination' },
    { type: 'flightno', label: 'Flight No.' },
    { type: 'sourcemarket', label: 'Source Market' },
    { type: 'acreg', label: 'Aircraft Registration' },
];

export const newCondition = (type: ConditionType = 'sourcemarket'): IConditionDraft => ({ key: ulid(), Type: type, Value: '' });
export const newConditionSet = (): IConditionSetDraft => ({ key: ulid(), Conditions: [newCondition()] });

export const toDrafts = (sets?: AudioFileConditionSet[]): IConditionSetDraft[] =>
    (sets ?? []).map(set => ({
        key: ulid(),
        Conditions: (set.Conditions ?? []).map(c => ({ key: ulid(), Type: c.Type ?? 'sourcemarket', Value: c.Value ?? '' })),
    }));

export const fromDrafts = (drafts: IConditionSetDraft[]): AudioFileConditionSet[] =>
    drafts.map(set => ({ Conditions: set.Conditions.map(c => ({ Type: c.Type, Value: c.Value })) }));

interface IOption {
    value: string;
    label: string;
}

// Lookup responses are lists of objects (Code/Name) or plain strings, optionally wrapped in Items
function toOptions(response: any): IOption[] {
    const list: any[] = Array.isArray(response) ? response : (response?.Items ?? []);
    const options = list.map((item): IOption | undefined => {
        if (typeof item === 'string') return { value: item, label: item };
        const value = item?.Code ?? item?.Registration ?? item?.Id ?? item?.Name ?? item.AcReg;
        if (!value) return undefined;
        const name = item?.Name && item.Name !== value ? ` - ${item.Name}` : '';
        return { value: String(value), label: `${value}${name}` };
    });
    return options.filter((o): o is IOption => !!o).sort((a, b) => a.label.localeCompare(b.label));
}

function ConditionSetsEditor({ value, onChange, showErrors = false }: IConditionSetsEditorProps) {
    // Lookups are loaded once and shared by all conditions.
    const { data: airlines } = useFetch<any>('/api/airline/airline');
    const { data: airports } = useFetch<any>('/api/airline/airport');
    const { data: sourceMarkets } = useFetch<any>('/api/airline/sourcemarket');
    const { data: aircraft } = useFetch<any>('/api/airline/aircraft');

    const optionsByType = useMemo<{ [key in ConditionType]?: IOption[] }>(() => ({
        airline: toOptions(airlines),
        dep: toOptions(airports),
        dest: toOptions(airports),
        sourcemarket: toOptions(sourceMarkets),
        acreg: toOptions(aircraft),
    }), [airlines, airports, sourceMarkets, aircraft]);

    const updateSet = (setKey: string, update: (set: IConditionSetDraft) => IConditionSetDraft) =>
        onChange(value.map(set => set.key === setKey ? update(set) : set));

    const updateCondition = (setKey: string, conditionKey: string, patch: Partial<IConditionDraft>) =>
        updateSet(setKey, set => ({ ...set, Conditions: set.Conditions.map(c => c.key === conditionKey ? { ...c, ...patch } : c) }));

    const renderValueInput = (set: IConditionSetDraft, condition: IConditionDraft) => {
        const error = showErrors && condition.Value.trim() === '';
        const helperText = error ? 'Select at least one value' : undefined;

        if (condition.Type === 'flightno') {
            return (
                <TextField fullWidth size='small' label='Value' placeholder='e.g. 123 or TOM123' value={condition.Value}
                    error={error} helperText={helperText}
                    onChange={e => updateCondition(set.key, condition.key, { Value: e.target.value })} />
            );
        }

        const options = optionsByType[condition.Type] ?? [];
        const selectedValues = condition.Value.split(VALUE_SEPARATOR).map(v => v.trim()).filter(Boolean);
        // Keep values that are not (yet) part of the loaded options selectable/visible.
        const selected: IOption[] = selectedValues.map(v => options.find(o => o.value === v) ?? { value: v, label: v });

        return (
            <Autocomplete
                multiple fullWidth size='small' disableCloseOnSelect
                options={options}
                value={selected}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                onChange={(_, selection) =>
                    updateCondition(set.key, condition.key, { Value: selection.map(o => o.value).join(VALUE_SEPARATOR) })}
                renderInput={(params) => <TextField {...params} label='Values' placeholder='Search…' error={error} helperText={helperText} />}
            />
        );
    };

    return (
        <Stack spacing={2}>
            {value.length === 0 && (
                <Typography variant='body2' color='text.secondary'>
                    No conditions defined. The audio file is not restricted.
                </Typography>
            )}

            {value.map((set, setIndex) => (
                <Box key={set.key}>
                    {setIndex > 0 && (
                        <Divider sx={{ mb: 2 }}><Typography variant='caption' color='text.secondary'>OR</Typography></Divider>
                    )}
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Typography variant='subtitle2' sx={{ flexGrow: 1 }}>Condition set {setIndex + 1}</Typography>
                            <Button size='small' color='error' startIcon={<SvgIcon component={TrashIcon} inheritViewBox />}
                                onClick={() => onChange(value.filter(s => s.key !== set.key))}>
                                Remove set
                            </Button>
                        </Box>

                        <Stack spacing={1.5}>
                            {set.Conditions.map((condition, conditionIndex) => (
                                <Box key={condition.key}>
                                    {conditionIndex > 0 && (
                                        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>AND</Typography>
                                    )}
                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'flex-start' }}>
                                        <TextField select size='small' label='Condition' sx={{ minWidth: 220 }} value={condition.Type}
                                            onChange={e => updateCondition(set.key, condition.key, { Type: e.target.value as ConditionType, Value: '' })}>
                                            {CONDITION_TYPES.map(t => <MenuItem key={t.type} value={t.type}>{t.label}</MenuItem>)}
                                        </TextField>
                                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>{renderValueInput(set, condition)}</Box>
                                        <Tooltip title={set.Conditions.length > 1 ? 'Remove condition' : 'A set needs at least one condition'}>
                                            <span>
                                                <IconButton color='error' disabled={set.Conditions.length <= 1}
                                                    onClick={() => updateSet(set.key, s => ({ ...s, Conditions: s.Conditions.filter(c => c.key !== condition.key) }))}>
                                                    <SvgIcon component={TrashIcon} inheritViewBox />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>

                        <Button size='small' sx={{ mt: 2 }} startIcon={<SvgIcon component={PlusIcon} inheritViewBox />}
                            onClick={() => updateSet(set.key, s => ({ ...s, Conditions: [...s.Conditions, newCondition()] }))}>
                            Add condition
                        </Button>
                    </Box>
                </Box>
            ))}

            <Box>
                <Button variant='outlined' startIcon={<SvgIcon component={PlusIcon} inheritViewBox />}
                    onClick={() => onChange([...value, newConditionSet()])}>
                    Add condition set
                </Button>
            </Box>
        </Stack>
    );
}

export default ConditionSetsEditor;
